/**
 * Feature plugin for Krux
 *
 * The Krux ControlTag is a tag manager that processes real time segments and sets segments in
 * local storage, and is included on all of Krux publisher's sites. It populates an LS key
 * `kxsegs_partner` for accounts with no namespace or `kx<namespace>_segs_partner` for accounts
 * with a namespace. Read more:
 *   https://confluence.sso.episerver.net/display/EXPENG/Krux+%3C%3E+Optimizely+Audience+Integration+SLA
 *
 * @module
 */
var _ = require('optimizely-client-fn');
var Logger = require('core/utils/logger');
var WindowEnv = require('core/env/window');



/**
 * If the project has no namespace specified, then the segments will be in this localStorage key.
 *   https://confluence.sso.episerver.net/display/EXPENG/Krux+%3C%3E+Optimizely+Audience+Integration+SLA
 * @private
 */
var KX_SEGMENTS_DEFAULT_KEY = 'kxsegs_partner';
var VENDOR = 'krux';

/**
 * Given a namespace, outputs the localStorage location where we can find Krux segments.
 *   https://confluence.sso.episerver.net/display/EXPENG/Krux+%3C%3E+Optimizely+Audience+Integration+SLA
 *
 * @param {string} kruxNamespace
 * @returns {string}
 * @private
 */
function buildKxSegmentsWithNamespace(kruxNamespace:string): string {
  return 'kx' + kruxNamespace + '_segs_partner';
}

/**
 * A comma-separated list of Krux segments of which the visitor is a member.
 * @typedef {string} KruxSegmentList
 */

/**
 * Gets Krux segments from local storage.
 * @param {Object} windowEnv
 * @returns {KruxSegmentList|null}
 * @private
 */
function getKruxLocalStorageValue(windowEnv:any, segmentLocation): KruxSegmentList|null {
  try {
    return windowEnv.getGlobal('localStorage') &&
           windowEnv.getGlobal('localStorage').getItem(segmentLocation) ||
           windowEnv.getGlobal('localStorage').getItem(KX_SEGMENTS_DEFAULT_KEY);
  } catch(e) {
    Logger.warn('Error reading from local storage for ' + VENDOR, ':', e);
  }
}


var pluginAudienceKrux: AudiencePluginConfig = {
  provider: {
    getter: [
      'stores/integration_settings',
      /**
       * @typedef {Object} KruxAudienceData
       * @property {string[]} audiences List of Krux audience IDs
       */

      /**
       * Gets audience segments from localStorage key (`kxsegs_partner` if no namespace is specified,
       * `kx<namespace>_segs_partner` if namespace is specified), and returns them as an array on the
       * `audiences` property.
       *
       * @param {Store} IntegrationSettingsStore
       * @return {KruxAudienceData|undefined}
       */
      function(IntegrationSettingsStore) {
        var kruxNamespace = IntegrationSettingsStore.get(VENDOR) &&
                            IntegrationSettingsStore.get(VENDOR).krux_namespace;

        var segmentLocation = KX_SEGMENTS_DEFAULT_KEY;
        if (kruxNamespace) {
          segmentLocation = buildKxSegmentsWithNamespace(kruxNamespace);
        }

        var kruxLocalStorageSegments = getKruxLocalStorageValue(WindowEnv, segmentLocation);

        if (_.isString(kruxLocalStorageSegments)) {
          return {
            audiences: kruxLocalStorageSegments.split(','),
          };
        }
      },
    ]
  },
  matcher: require('core/plugins/matchers/key_value'),
  vendor: VENDOR,
};

module.exports = function(PluginManager) {
  PluginManager.registerAudiencePlugin(pluginAudienceKrux);
};

if (__TEST__) {
  module.exports.plugin = pluginAudienceKrux;
}
